import React, { useState, useEffect } from 'react';
import { UserPlus, Search, Filter, Edit, Trash2, Check, X, AlertCircle, Users, Phone, HeartPulse, Eye } from 'lucide-react';
import { collection, getDocs, addDoc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { authService } from '../../../services/authService';
import userAdministrationService from '../../../services/userAdministrationService';

const UserAdministrationPanel = () => {
  const [activeTab, setActiveTab] = useState('staff'); // 'staff' or 'patients'
  const [users, setUsers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state for user creation/editing
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'nurse',
    employee_id: '',
    phone_number: '',
    department: '',
    specialization: '',
    license_number: '',
    employment_status: 'active'
  });

  // Static reference data - roles
  const roles = [
    { value: 'admin', label: 'Administrator', color: 'bg-red-500' },
    { value: 'doctor', label: 'Doctor', color: 'bg-blue-500' },
    { value: 'nurse', label: 'Nurse', color: 'bg-green-500' },
    { value: 'lab_technician', label: 'Lab Technician', color: 'bg-purple-500' },
    { value: 'pharmacist', label: 'Pharmacist', color: 'bg-orange-500' },
    { value: 'receptionist', label: 'Receptionist', color: 'bg-gray-500' },
    { value: 'billing_clerk', label: 'Billing Clerk', color: 'bg-emerald-500' },
    { value: 'accountant', label: 'Accountant', color: 'bg-teal-500' },
    { value: 'finance_manager', label: 'Finance Manager', color: 'bg-cyan-500' },
    { value: 'auditor', label: 'Auditor', color: 'bg-indigo-500' }
  ];

  // Static reference data - medical specializations
  const specializations = [
    { value: '', label: 'Select Specialization' },
    // Primary Care
    { value: 'general_practice', label: 'General Practice / Family Medicine' },
    { value: 'internal_medicine', label: 'Internal Medicine' },
    { value: 'pediatrics', label: 'Pediatrics' },
    { value: 'geriatrics', label: 'Geriatrics' },
    // Surgical Specialties
    { value: 'general_surgery', label: 'General Surgery' },
    { value: 'orthopedic_surgery', label: 'Orthopedic Surgery' },
    { value: 'neurosurgery', label: 'Neurosurgery' },
    { value: 'cardiothoracic_surgery', label: 'Cardiothoracic Surgery' },
    { value: 'plastic_surgery', label: 'Plastic Surgery' },
    { value: 'urology', label: 'Urology' },
    // Medical Specialties
    { value: 'cardiology', label: 'Cardiology' },
    { value: 'pulmonology', label: 'Pulmonology' },
    { value: 'gastroenterology', label: 'Gastroenterology' },
    { value: 'nephrology', label: 'Nephrology' },
    { value: 'endocrinology', label: 'Endocrinology' },
    { value: 'rheumatology', label: 'Rheumatology' },
    { value: 'hematology', label: 'Hematology' },
    { value: 'oncology', label: 'Oncology' },
    { value: 'infectious_disease', label: 'Infectious Disease' },
    { value: 'neurology', label: 'Neurology' },
    // Women's Health
    { value: 'obstetrics_gynecology', label: 'Obstetrics & Gynecology' },
    { value: 'maternal_fetal_medicine', label: 'Maternal-Fetal Medicine' },
    // Mental Health
    { value: 'psychiatry', label: 'Psychiatry' },
    { value: 'clinical_psychology', label: 'Clinical Psychology' },
    // Diagnostic & Support
    { value: 'radiology', label: 'Radiology' },
    { value: 'pathology', label: 'Pathology' },
    { value: 'anesthesiology', label: 'Anesthesiology' },
    { value: 'emergency_medicine', label: 'Emergency Medicine' },
    { value: 'critical_care', label: 'Critical Care / ICU' },
    // Other Specialties
    { value: 'dermatology', label: 'Dermatology' },
    { value: 'ophthalmology', label: 'Ophthalmology' },
    { value: 'otolaryngology', label: 'ENT (Otolaryngology)' },
    { value: 'physical_medicine', label: 'Physical Medicine & Rehabilitation' },
    { value: 'allergy_immunology', label: 'Allergy & Immunology' },
    // Nursing Specializations
    { value: 'registered_nurse', label: 'Registered Nurse (RN)' },
    { value: 'nurse_practitioner', label: 'Nurse Practitioner (NP)' },
    { value: 'critical_care_nurse', label: 'Critical Care Nurse' },
    { value: 'pediatric_nurse', label: 'Pediatric Nurse' },
    { value: 'oncology_nurse', label: 'Oncology Nurse' },
    { value: 'midwife', label: 'Certified Nurse Midwife' },
    // Lab & Pharmacy
    { value: 'clinical_pathology', label: 'Clinical Pathology' },
    { value: 'microbiology', label: 'Microbiology' },
    { value: 'clinical_pharmacy', label: 'Clinical Pharmacy' },
    { value: 'hospital_pharmacy', label: 'Hospital Pharmacy' },
    // Other
    { value: 'other', label: 'Other' }
  ];

  // Default departments (used if Firestore collection is empty)
  const defaultDepartments = [
    { id: 'emergency', name: 'Emergency Department', code: 'ED' },
    { id: 'outpatient', name: 'Outpatient Department', code: 'OPD' },
    { id: 'inpatient', name: 'Inpatient Department', code: 'IPD' },
    { id: 'surgery', name: 'Surgery', code: 'SUR' },
    { id: 'pediatrics', name: 'Pediatrics', code: 'PED' },
    { id: 'obstetrics', name: 'Obstetrics & Gynecology', code: 'OBG' },
    { id: 'radiology', name: 'Radiology', code: 'RAD' },
    { id: 'laboratory', name: 'Laboratory', code: 'LAB' },
    { id: 'pharmacy', name: 'Pharmacy', code: 'PHR' },
    { id: 'icu', name: 'Intensive Care Unit', code: 'ICU' },
    { id: 'cardiology', name: 'Cardiology', code: 'CAR' },
    { id: 'orthopedics', name: 'Orthopedics', code: 'ORT' },
    { id: 'neurology', name: 'Neurology', code: 'NEU' },
    { id: 'psychiatry', name: 'Psychiatry', code: 'PSY' },
    { id: 'administration', name: 'Administration', code: 'ADM' },
    { id: 'finance', name: 'Finance & Accounts', code: 'FIN' }
  ];

  // Load users and departments
  useEffect(() => {
    loadUsers();
    loadDepartments();
    loadPatients();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await userAdministrationService.getAllUsers();
      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      setError('Failed to load users: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      // Try to load from Firestore
      const { data, error } = await userAdministrationService.getAllDepartments();

      if (error || !data || data.length === 0) {
        console.log('No departments in Firestore, using defaults and seeding...');
        // Use default departments
        setDepartments(defaultDepartments);

        // Optionally seed Firestore with default departments
        try {
          const deptCollection = collection(db, 'departments');
          for (const dept of defaultDepartments) {
            await addDoc(deptCollection, {
              ...dept,
              created_at: Timestamp.now(),
              is_active: true
            });
          }
          console.log('✓ Seeded departments collection');
        } catch (seedError) {
          console.warn('Could not seed departments:', seedError);
        }
      } else {
        setDepartments(data);
      }
    } catch (error) {
      console.log('Failed to load departments, using defaults:', error?.message);
      setDepartments(defaultDepartments);
    }
  };

  const loadPatients = async () => {
    try {
      const patientsQuery = query(
        collection(db, 'patients'),
        orderBy('created_at', 'desc')
      );
      const snapshot = await getDocs(patientsQuery);
      const patientsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPatients(patientsData);
    } catch (error) {
      console.error('Failed to load patients:', error?.message);
      setPatients([]);
    }
  };

  const handleCreateUser = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Create auth user + profile via authService
      // Note: This uses createUserWithEmailAndPassword which signs in as the new user
      const { data: userCredential, error: signUpError } = await authService.signUp({
        email: formData?.email,
        password: formData?.password,
        full_name: formData?.full_name,
        role: formData?.role,
        employee_id: formData?.employee_id,
        department: formData?.department
      });

      if (signUpError) throw new Error(signUpError?.message || 'Failed to create user');

      // Get the uid from the userCredential - it's at userCredential.user.uid
      const uid = userCredential?.user?.uid;
      console.log('Created user with UID:', uid);

      if (!uid) {
        throw new Error('Failed to get user ID from authentication response');
      }

      // Ensure profile has all desired fields
      await userAdministrationService.updateUser(uid, {
        email: formData?.email,
        full_name: formData?.full_name,
        role: formData?.role,
        employee_id: formData?.employee_id,
        phone_number: formData?.phone_number,
        department: formData?.department,
        specialization: formData?.specialization,
        license_number: formData?.license_number,
        employment_status: formData?.employment_status
      });

      // Assign to department if selected
      if (formData?.department) {
        const allDepts = departments?.length > 0 ? departments : defaultDepartments;
        const selectedDept = allDepts?.find(d => d?.name === formData?.department);
        console.log('Department assignment:', { selectedDept, department: formData?.department });

        if (selectedDept?.id) {
          const { error: assignError } = await userAdministrationService.assignUserToDepartment(uid, selectedDept.id, uid);
          if (assignError) {
            console.warn('Department assignment failed:', assignError);
            // Don't throw - user was created successfully, just log the warning
          }
        }
      }

      setSuccess('User created successfully!');
      setShowCreateModal(false);
      resetForm();
      loadUsers();
    } catch (error) {
      console.error('Create user error:', error);
      setError('Failed to create user: ' + (error?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e?.preventDefault();
    if (!selectedUser) return;

    setLoading(true);
    setError('');

    try {
      const { data, error } = await userAdministrationService.updateUser(selectedUser?.id, {
        full_name: formData?.full_name,
        role: formData?.role,
        employee_id: formData?.employee_id,
        phone_number: formData?.phone_number,
        department: formData?.department,
        specialization: formData?.specialization,
        license_number: formData?.license_number,
        employment_status: formData?.employment_status
      });

      if (error) throw error;

      setSuccess('User updated successfully!');
      setShowEditModal(false);
      resetForm();
      loadUsers();
    } catch (error) {
      setError('Failed to update user: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!confirm(`Are you sure you want to delete ${user?.full_name}? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      // Note: In production, you might want to deactivate instead of delete
      const { data, error } = await userAdministrationService.updateUserStatus(user?.id, 'terminated');
      if (error) throw error;

      setSuccess('User deactivated successfully!');
      loadUsers();
    } catch (error) {
      setError('Failed to deactivate user: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'nurse',
      employee_id: '',
      phone_number: '',
      department: '',
      specialization: '',
      license_number: '',
      employment_status: 'active'
    });
    setSelectedUser(null);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user?.email || '',
      password: '', // Never populate password
      full_name: user?.full_name || '',
      role: user?.role || 'nurse',
      employee_id: user?.employee_id || '',
      phone_number: user?.phone_number || '',
      department: user?.department || '',
      specialization: user?.specialization || '',
      license_number: user?.license_number || '',
      employment_status: user?.employment_status || 'active'
    });
    setShowEditModal(true);
  };

  // Filter users based on search and filters
  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      user?.employee_id?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesRole = filterRole === 'all' || user?.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user?.employment_status === filterStatus;

    return matchesSearch && matchesRole && matchesStatus;
  });

  // Filter patients based on search
  const filteredPatients = patients?.filter(patient => {
    const searchLower = patientSearchTerm?.toLowerCase();
    return patient?.full_name?.toLowerCase()?.includes(searchLower) ||
      patient?.mrn?.toLowerCase()?.includes(searchLower) ||
      patient?.phone_number?.includes(patientSearchTerm) ||
      patient?.email?.toLowerCase()?.includes(searchLower);
  });

  const getRoleInfo = (role) => {
    return roles?.find(r => r?.value === role) || { label: role, color: 'bg-gray-500' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Administration</h2>
          <p className="text-gray-600">Manage hospital staff and patient accounts</p>
        </div>
        {activeTab === 'staff' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            <span>Add User</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('staff')}
            className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
              activeTab === 'staff'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="h-4 w-4" />
            <span>Staff ({users?.length || 0})</span>
          </button>
          <button
            onClick={() => setActiveTab('patients')}
            className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
              activeTab === 'patients'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <HeartPulse className="h-4 w-4" />
            <span>Patients ({patients?.length || 0})</span>
          </button>
        </nav>
      </div>

      {/* Staff Tab Content */}
      {activeTab === 'staff' && (
        <>
          {/* Staff Filters */}
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Role Filter */}
              <div className="sm:w-48">
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Roles</option>
                  {roles?.map(role => (
                    <option key={role?.value} value={role?.value}>{role?.label}</option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div className="sm:w-48">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="on_leave">On Leave</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Patients Tab Content */}
      {activeTab === 'patients' && (
        <>
          {/* Patients Search */}
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search patients by name, MRN, phone, or email..."
                    value={patientSearchTerm}
                    onChange={(e) => setPatientSearchTerm(e?.target?.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-sm text-green-700">{success}</span>
            <button onClick={() => setSuccess('')} className="ml-auto">
              <X className="h-4 w-4 text-green-500" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
            <button onClick={() => setError('')} className="ml-auto">
              <X className="h-4 w-4 text-red-500" />
            </button>
          </div>
        </div>
      )}

      {/* Users Table - Staff Tab */}
      {activeTab === 'staff' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    </td>
                  </tr>
                ) : filteredUsers?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers?.map((user) => {
                    const roleInfo = getRoleInfo(user?.role);
                    const department = user?.staff_assignments?.[0]?.departments?.name || user?.department || 'Not assigned';

                    return (
                      <tr key={user?.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                                {user?.photo_url ? (
                                  <img src={user.photo_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-sm font-medium text-gray-600">
                                    {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{user?.full_name}</div>
                              <div className="text-sm text-gray-500">{user?.email}</div>
                              {user?.employee_id && (
                                <div className="text-xs text-gray-400">ID: {user?.employee_id}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${roleInfo?.color}`}>
                            {roleInfo?.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {department}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user?.employment_status === 'active' ? 'bg-green-100 text-green-800' :
                            user?.employment_status === 'inactive' ? 'bg-red-100 text-red-800' :
                              user?.employment_status === 'on_leave' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                            {user?.employment_status?.replace('_', ' ')?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user?.created_at ? (() => { const d = new Date(user?.created_at); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; })() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openEditModal(user)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit user"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600 hover:text-red-900"
                              title="Deactivate user"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Patients Table - Patients Tab */}
      {activeTab === 'patients' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MRN
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    </td>
                  </tr>
                ) : filteredPatients?.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                      No patients found
                    </td>
                  </tr>
                ) : (
                  filteredPatients?.map((patient) => (
                    <tr key={patient?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-rose-100 flex items-center justify-center overflow-hidden">
                              {patient?.photo_url ? (
                                <img src={patient.photo_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-sm font-medium text-rose-600">
                                  {patient?.full_name?.charAt(0)?.toUpperCase() || 'P'}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{patient?.full_name}</div>
                            {patient?.gender && (
                              <div className="text-xs text-gray-500">{patient?.gender}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {patient?.mrn || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient?.phone_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {patient?.email || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          patient?.registration_status === 'complete' ? 'bg-green-100 text-green-800' :
                          patient?.registration_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {patient?.registration_status?.toUpperCase() || 'REGISTERED'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {patient?.created_at ? (() => { 
                          const d = patient?.created_at?.toDate ? patient.created_at.toDate() : new Date(patient?.created_at); 
                          return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; 
                        })() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => { setSelectedPatient(patient); setShowPatientModal(true); }}
                          className="text-blue-600 hover:text-blue-900"
                          title="View patient details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Create New User</h2>
                <button
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData?.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData?.email}
                    onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={formData?.password}
                    onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                    minLength="6"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role *
                  </label>
                  <select
                    value={formData?.role}
                    onChange={(e) => setFormData({ ...formData, role: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {roles?.map(role => (
                      <option key={role?.value} value={role?.value}>{role?.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee ID
                  </label>
                  <input
                    type="text"
                    value={formData?.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData?.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department *
                  </label>
                  <select
                    value={formData?.department}
                    onChange={(e) => setFormData({ ...formData, department: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Department</option>
                    {(departments?.length > 0 ? departments : defaultDepartments).map(dept => (
                      <option key={dept?.id} value={dept?.name}>{dept?.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specialization
                  </label>
                  <select
                    value={formData?.specialization}
                    onChange={(e) => setFormData({ ...formData, specialization: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {specializations.map(spec => (
                      <option key={spec.value} value={spec.value}>{spec.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    License Number
                  </label>
                  <input
                    type="text"
                    value={formData?.license_number}
                    onChange={(e) => setFormData({ ...formData, license_number: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employment Status
                  </label>
                  <select
                    value={formData?.employment_status}
                    onChange={(e) => setFormData({ ...formData, employment_status: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="on_leave">On Leave</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Edit User</h2>
                <button
                  onClick={() => { setShowEditModal(false); resetForm(); }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <form onSubmit={handleUpdateUser} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData?.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData?.email}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role *
                  </label>
                  <select
                    value={formData?.role}
                    onChange={(e) => setFormData({ ...formData, role: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {roles?.map(role => (
                      <option key={role?.value} value={role?.value}>{role?.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee ID
                  </label>
                  <input
                    type="text"
                    value={formData?.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData?.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department *
                  </label>
                  <select
                    value={formData?.department}
                    onChange={(e) => setFormData({ ...formData, department: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Department</option>
                    {(departments?.length > 0 ? departments : defaultDepartments).map(dept => (
                      <option key={dept?.id} value={dept?.name}>{dept?.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specialization
                  </label>
                  <select
                    value={formData?.specialization}
                    onChange={(e) => setFormData({ ...formData, specialization: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {specializations.map(spec => (
                      <option key={spec.value} value={spec.value}>{spec.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    License Number
                  </label>
                  <input
                    type="text"
                    value={formData?.license_number}
                    onChange={(e) => setFormData({ ...formData, license_number: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employment Status
                  </label>
                  <select
                    value={formData?.employment_status}
                    onChange={(e) => setFormData({ ...formData, employment_status: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="on_leave">On Leave</option>
                    <option value="terminated">Terminated</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Patient Details Modal */}
      {showPatientModal && selectedPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Patient Details</h2>
                <button
                  onClick={() => { setShowPatientModal(false); setSelectedPatient(null); }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Patient Photo & Name */}
              <div className="flex items-center space-x-4">
                <div className="h-16 w-16 rounded-full bg-rose-100 flex items-center justify-center overflow-hidden">
                  {selectedPatient?.photo_url ? (
                    <img src={selectedPatient.photo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xl font-medium text-rose-600">
                      {selectedPatient?.full_name?.charAt(0)?.toUpperCase() || 'P'}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedPatient?.full_name}</h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    MRN: {selectedPatient?.mrn || 'N/A'}
                  </span>
                </div>
              </div>

              {/* Patient Details Grid */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Phone</label>
                  <p className="text-sm text-gray-900">{selectedPatient?.phone_number || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Email</label>
                  <p className="text-sm text-gray-900">{selectedPatient?.email || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Gender</label>
                  <p className="text-sm text-gray-900">{selectedPatient?.gender || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Date of Birth</label>
                  <p className="text-sm text-gray-900">{selectedPatient?.date_of_birth || '-'}</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-500 uppercase">Address</label>
                  <p className="text-sm text-gray-900">{selectedPatient?.address || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">State</label>
                  <p className="text-sm text-gray-900">{selectedPatient?.state || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase">Status</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedPatient?.registration_status === 'complete' ? 'bg-green-100 text-green-800' :
                    selectedPatient?.registration_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedPatient?.registration_status?.toUpperCase() || 'REGISTERED'}
                  </span>
                </div>
              </div>

              {/* Emergency Contact */}
              {selectedPatient?.emergency_contact_name && (
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Emergency Contact</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">Name</label>
                      <p className="text-sm text-gray-900">{selectedPatient?.emergency_contact_name}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase">Phone</label>
                      <p className="text-sm text-gray-900">{selectedPatient?.emergency_contact_phone || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => { setShowPatientModal(false); setSelectedPatient(null); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAdministrationPanel;